// ============================================================================
// The two named refusals rpc_asset_move_by_name raises, turned into something a
// screen can render.
//
// WHY THIS IS ITS OWN PURE FILE. The database deliberately refuses to guess:
// rather than silently taking the first of several matches, it raises
// DESTINATION_AMBIGUOUS and puts the candidate list in the message. That list
// is the only place the candidates exist — the call already failed, so there is
// no result set to read them from. Parsing a message string is fragile by
// nature, so it happens once, here, with tests against the exact strings the
// live function raises, instead of inline in a component where a wording change
// would silently degrade to "something went wrong".
//
// ⛔ PARSE THE RAW ERROR, NOT A TRANSLATED ONE. shared/errors.ts rewords
// Postgres for humans (TCSD-0110); run it over one of these and the machine
// -readable prefix is gone. Call this first, fall back to errorMessage().
//
// The exact raisers, from the live function body on 2026-08-27:
//   DESTINATION_NOT_FOUND: no office or active facility matches "%"
//   DESTINATION_AMBIGUOUS: % matches for "%" — <kind> <ID> — <name>; …
// ============================================================================

export interface DestinationCandidate {
  kind: 'office' | 'facility';
  id: string;
  name: string;
}

export type DestinationRefusal =
  | { code: 'not_found'; query: string; candidates: [] }
  | { code: 'ambiguous'; query: string; candidates: DestinationCandidate[] };

/** Pull the quoted query out of either message shape. */
function quotedQuery(msg: string): string {
  const m = /"([^"]*)"/.exec(msg);
  return m ? m[1] : '';
}

/**
 * The candidate list, from the tail of an AMBIGUOUS message.
 *
 * Split on the ENTRY MARKERS rather than on '; ', because a facility name may
 * legitimately contain a semicolon or a dash and splitting on the separator
 * would quietly cut a name in half. Each entry starts `<kind> <ID> — `, so the
 * markers are found first and the name is whatever lies between one marker and
 * the next.
 */
function parseCandidates(msg: string): DestinationCandidate[] {
  const marker = /(office|facility) ([A-Z]{2,4}-\d+) — /g;
  const hits: { kind: 'office' | 'facility'; id: string; from: number; to: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = marker.exec(msg)) !== null) {
    hits.push({
      kind: m[1] as 'office' | 'facility',
      id: m[2],
      from: m.index,
      to: marker.lastIndex,
    });
  }
  return hits.map((h, i) => {
    const end = i + 1 < hits.length ? hits[i + 1].from : msg.length;
    const name = msg.slice(h.to, end).replace(/;\s*$/, '').trim();
    return { kind: h.kind, id: h.id, name };
  });
}

/**
 * Recognise one of the two named refusals. Returns null for anything else —
 * including a permission refusal, a bad status or a network failure — so the
 * caller falls through to its normal error handling rather than mislabelling
 * an unrelated failure as a naming problem.
 */
export function parseDestinationRefusal(err: unknown): DestinationRefusal | null {
  const e = err as { message?: unknown; details?: unknown; hint?: unknown } | null;
  const msg = [e?.message, e?.details, e?.hint]
    .map((x) => (typeof x === 'string' ? x : ''))
    .join(' ');

  if (msg.includes('DESTINATION_NOT_FOUND')) {
    return { code: 'not_found', query: quotedQuery(msg), candidates: [] };
  }
  if (msg.includes('DESTINATION_AMBIGUOUS')) {
    return { code: 'ambiguous', query: quotedQuery(msg), candidates: parseCandidates(msg) };
  }
  return null;
}

/** The sentence shown for a NOT_FOUND. One place, so the wording is testable. */
export function notFoundMessage(query: string): string {
  return `No office or facility matches “${query}”. Try a shorter word.`;
}
